import { DialogComponent, DialogCloseEvent } from '@theme/dialog';

/**
 * Shared "Notify me when back in stock" dialog.
 * Opened by product card buttons via `on:click="#BackInStockDialog/open"`, which carry the
 * product details in `data-product-title`, `data-product-url` and `data-variant-title`.
 * Submits Shopify's contact form in the background; if the storefront asks for a captcha
 * challenge, it falls back to a regular form submission so the shopper can complete it.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLElement} productLabel
 * @property {HTMLElement} formFields
 * @property {HTMLInputElement} productField
 * @property {HTMLInputElement} variantField
 * @property {HTMLInputElement} urlField
 * @property {HTMLInputElement} emailInput
 * @property {HTMLButtonElement} submitButton
 * @property {HTMLElement} status
 *
 * @extends DialogComponent<Refs>
 */
class BackInStockDialog extends DialogComponent {
  requiredRefs = ['dialog', 'productField', 'variantField', 'urlField', 'emailInput', 'submitButton', 'status'];

  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {HTMLElement | null} */
  #opener = null;


  connectedCallback() {
    super.connectedCallback();
    this.form?.addEventListener('submit', this.handleSubmit);
    this.addEventListener(DialogCloseEvent.eventName, this.#restoreFocus);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.form?.removeEventListener('submit', this.handleSubmit);
    this.removeEventListener(DialogCloseEvent.eventName, this.#restoreFocus);
    this.#abortController?.abort();
  }

  /** @returns {HTMLFormElement | null} */
  get form() {
    return this.querySelector('form');
  }

  /**
   * Opens the dialog for the product described by the triggering button.
   * @param {Event} event
   */
  open = (event) => {
    const trigger = event.target instanceof HTMLElement ? event.target : null;
    const { productLabel, formFields, productField, variantField, urlField, status, submitButton } = this.refs;

    const title = trigger?.dataset.productTitle ?? '';
    const variant = trigger?.dataset.variantTitle ?? '';

    productField.value = title;
    variantField.value = variant;
    urlField.value = trigger?.dataset.productUrl ?? '';
    const variantIsInTitle = variant && title.toLowerCase().includes(variant.toLowerCase());
    if (productLabel) productLabel.textContent = variant && !variantIsInTitle ? `${title} – ${variant}` : title;

    if (formFields) formFields.hidden = false;
    status.textContent = '';
    delete status.dataset.state;
    submitButton.removeAttribute('aria-busy');

    this.#opener = trigger;
    this.showDialog();
    requestAnimationFrame(() => requestAnimationFrame(() => this.refs.emailInput.focus()));
  };

  /** Returns focus to the card button that opened the dialog. */
  #restoreFocus = () => {
    this.#opener?.focus({ preventScroll: true });
    this.#opener = null;
  };

  /**
   * @param {SubmitEvent} event
   */
  handleSubmit = async (event) => {
    const form = this.form;
    if (!form) return;

    event.preventDefault();
    if (!form.reportValidity()) return;

    const { submitButton, status, formFields } = this.refs;

    this.#abortController?.abort();
    this.#abortController = new AbortController();
    submitButton.setAttribute('aria-busy', 'true');
    status.textContent = '';
    delete status.dataset.state;

    try {
      const response = await fetch(form.action, {
        method: 'POST',
        body: new FormData(form),
        signal: this.#abortController.signal,
      });

      const responseUrl = new URL(response.url);

      // Spam protection: let the browser submit normally so the captcha page can be completed.
      if (responseUrl.pathname.includes('/challenge')) {
        form.removeEventListener('submit', this.handleSubmit);
        form.submit();
        return;
      }

      if (!response.ok || !responseUrl.searchParams.has('contact_posted')) {
        throw new Error(`Back in stock request failed: ${response.status}`);
      }

      form.reset();
      if (formFields) formFields.hidden = true;
      status.dataset.state = 'success';
      status.textContent = status.dataset.success ?? '';
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      console.error(error);
      status.dataset.state = 'error';
      status.textContent = status.dataset.error ?? '';
    } finally {
      submitButton.removeAttribute('aria-busy');
    }
  };
}

if (!customElements.get('back-in-stock-dialog')) {
  customElements.define('back-in-stock-dialog', BackInStockDialog);
}
